import { EventManager } from "../events";
import { proxiedFetch } from "../requests";

export enum ResourceType {
	image = "image",
	css = "css",
	script = "script",
	link = "link",
}

export enum WatcherEvent {
	result = "result",
	resource = "resource",
	regex = "regex",
	finished = "finished",
}

type WatcherEventToDataMap = {
	result: { result: WatcherResult; progress: number };
	resource: WatcherResourceResult;
	regex: WatcherRegexResult;
	finished: null;
};

// used to assign a unique id to each result
let resultIdSequence = 1;

export class Watcher {
	private _urls: string[];
	private _totalUrls: number;
	public events: EventManager<WatcherEventToDataMap>;

	constructor(urls: string[]) {
		this._urls = urls;
		this._totalUrls = urls.length;
		this.events = new EventManager();
	}

	async testNextUrl({
		regexes,
		randomize,
	}: {
		regexes?: WatcherRegex[];
		randomize?: boolean;
	}) {
		if (this._urls.length === 0) {
			return;
		}

		let url;
		if (randomize) {
			const index = Math.floor(Math.random() * this._urls.length);
			[url] = this._urls.splice(index, 1);
		} else {
			url = this._urls.shift() as string;
		}

		// make request
		const timeStarted = Date.now();
		const response = await proxiedFetch(url);
		const timeEnded = Date.now();

		// parse HTML
		const parser = new DOMParser();
		const html = await response.text();
		const root = parser.parseFromString(html, "text/html");

		// test regexes
		if (regexes) {
			for (const { name, regex } of regexes) {
				const re = new RegExp(regex, "g");
				const matches = [...html.matchAll(re)];
				this.events.notify("regex", {
					id: resultIdSequence++,
					name,
					url,
					matched: matches.length !== 0,
					matches: matches.map(
						match => match.groups?.result ?? match[0]
					),
				});
			}
		}

		// extract text of title element
		const titleNode = root.querySelector("title");
		const title =
			titleNode?.textContent == null
				? "undefined"
				: titleNode.textContent;

		// extract text of description meta element
		const descriptionNode = root.querySelector("meta[name='description']");
		const description = descriptionNode?.getAttribute("content") || null;

		// extract text of h1 element
		const captionNode = root.querySelector("h1");
		const caption =
			captionNode?.textContent == null ? null : captionNode.textContent;

		// extract content of robots meta element
		const robotsNode = root.querySelector("meta[name='robots']");
		const robots = robotsNode?.getAttribute("content") || null;

		// search for an 'ads.js' script tag
		const scriptNodes = root.querySelectorAll("script");
		let ads = false;
		for (const scriptNode of scriptNodes) {
			if (scriptNode && scriptNode.src.indexOf("ads.js") !== -1) {
				ads = true;
				break;
			}
		}

		const resources = this._parseResources(root, url);

		const result: WatcherResult = {
			id: resultIdSequence++,
			url,
			status: response.status,
			durationMS: timeEnded - timeStarted,
			title,
			caption,
			description,
			robots,
			ads,
			resources,
		};

		const remainingUrlsCount = this._urls.length;
		const progress = 1 - remainingUrlsCount / this._totalUrls;
		this.events.notify("result", { result, progress });

		if (remainingUrlsCount === 0) {
			this.events.notify("finished");
		}
	}

	_parseResources(root: Document, baseUrl: string) {
		const url = new URL(baseUrl);
		const cleanUrl = baseUrl.slice(
			0,
			baseUrl.length - url.pathname.length - url.search.length
		);

		const parsedResources: ParsedResourceInfo[] = [];

		const buildFullUrl = (resourceUrl: string) => {
			return resourceUrl.startsWith("/")
				? cleanUrl + resourceUrl
				: resourceUrl;
		};

		const addResource = (
			resourceUrl: string,
			type: ResourceType,
			anchor?: string
		) => {
			parsedResources.push({
				type,
				url: buildFullUrl(resourceUrl),
				anchor,
			});
		};

		// extract image urls from page
		const imageNodes = root.querySelectorAll("img");
		for (const imageNode of imageNodes) {
			const imageUrl = imageNode.getAttribute("src");
			const altText = imageNode.getAttribute("alt");
			if (imageUrl) {
				addResource(imageUrl, ResourceType.image, altText || undefined);
			}
		}

		// extract css stylesheets urls from page
		const cssNodes = root.querySelectorAll("link[rel='stylesheet']");
		for (const cssNode of cssNodes) {
			const cssUrl = cssNode.getAttribute("href");
			if (cssUrl) {
				addResource(cssUrl, ResourceType.css);
			}
		}

		// extract images from inline CSS style
		const backgroundImageRegex = /background-image:\s*url\((?<url>.*?)\)/;
		const inlineStyleNodes = root.querySelectorAll("[style]");
		for (const inlineStyleNode of inlineStyleNodes) {
			const styleAttr = inlineStyleNode.getAttribute("style");
			const match = styleAttr!.match(backgroundImageRegex);
			if (match?.groups?.url) {
				const bgImageUrl = match.groups.url.slice(1, -1);
				addResource(bgImageUrl, ResourceType.image, "background-image");
			}
		}

		// extract scripts from page
		const scriptNodes = root.querySelectorAll("script[src]");
		for (const scriptNode of scriptNodes) {
			const scriptUrl = scriptNode.getAttribute("src");
			if (scriptUrl) {
				addResource(scriptUrl, ResourceType.script);
			}
		}

		// NOTE: specific to unipage
		// extract links from the article
		const articleElement = root.querySelector(".page-content");
		if (articleElement) {
			const linkNodes = articleElement.querySelectorAll("a");
			for (const linkNode of linkNodes) {
				const href = linkNode.getAttribute("href");
				const text = linkNode.textContent?.trim();
				if (href && href.startsWith("http")) {
					addResource(href, ResourceType.link, text || undefined);
				}
			}
		}

		return parsedResources;
	}

	public static async testResource(
		resource: ParsedResourceInfo
	): Promise<WatcherResourceResult> {
		const _testResource = async (method: "HEAD" | "GET") => {
			const timeStarted = Date.now();
			const response = await proxiedFetch(resource.url, {
				method,
			});
			const timeEnded = Date.now();
			const proxyRequestTime = response.headers.get(
				"X-Proxy-Request-Time"
			);
			const durationMS = proxyRequestTime
				? parseInt(proxyRequestTime)
				: timeEnded - timeStarted;
			return { response, durationMS };
		};

		let { response, durationMS } = await _testResource("HEAD");
		// NOTE: some websites handle HEAD request incorrectly so we send a GET
		// request to verify the error
		if (!response.ok && resource.type === ResourceType.link) {
			({ response, durationMS } = await _testResource("GET"));
		}

		const resourceResult: WatcherResourceResult = {
			id: resultIdSequence++,
			type: resource.type,
			url: resource.url,
			status: response.status,
			durationMS,
			anchor: resource.anchor,
		};

		return resourceResult;
	}
}

export type WatcherResult = {
	id: number;
	url: string;
	status: number;
	durationMS: number;
	title: string;
	caption: string | null;
	description: string | null;
	robots: string | null;
	ads: boolean;

	resources: ParsedResourceInfo[];
};

export type WatcherResourceResult = {
	id: number;
	type: ResourceType;
	url: string;
	status: number;
	durationMS: number;
	anchor?: string;
};

export type WatcherSubscriber = (
	result: WatcherResult,
	progress: number
) => void;

export type WatcherResourceSubscriber = (result: WatcherResourceResult) => void;

export type ParsedResourceInfo = {
	type: ResourceType;
	url: string;
	anchor?: string;
};

export type WatcherRegex = {
	name: string;
	regex: string;
};

export type WatcherRegexResult = {
	id: number;
	name: string;
	url: string;
	matched: boolean;
	matches?: string[];
};
